<script setup lang="ts">
import { computed, onMounted, ref, useAttrs, useId, useSlots, watch } from 'vue'
import { CHOICE_CONTROL } from './controlStyles'

defineOptions({ inheritAttrs: false })

interface Props {
  /** Boolean for a single checkbox, or an array when several share one model. */
  modelValue?: boolean | (string | number)[]
  /** Required only in array mode — the value this box contributes. */
  value?: string | number
  label?: string
  description?: string
  disabled?: boolean
  /**
   * Third state for a select-all box: some but not all children checked.
   * `indeterminate` is a DOM property, not an attribute, so it cannot be bound
   * in the template and has to be written to the element directly.
   */
  indeterminate?: boolean
  id?: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean | (string | number)[]): void
}>()

const attrs = useAttrs()
const uid = useId()
/**
 * `class` is routed to the wrapper row, every other attribute to the control.
 * Without this a caller styling the row (padding, hover) would instead style
 * the 16px box, and wrapping the component in their own <label> would nest one
 * label inside another.
 */
const rootClass = computed(() => attrs.class as unknown)

/**
 * With no label, description or slot there is nothing to lay out, so render the
 * control alone. That makes this a true drop-in for the many existing
 * <label>…<input>…<span></label> rows, whose wrapper markup stays untouched.
 */
const slots = useSlots()
const bare = computed(() => !props.label && !props.description && !slots.default)
const controlAttrs = computed(() => {
  const { class: _row, ...rest } = attrs
  return rest
})

const controlId = computed(() => props.id ?? `chk-${uid}`)

const el = ref<HTMLInputElement | null>(null)

function syncIndeterminate() {
  if (el.value) el.value.indeterminate = !!props.indeterminate
}
onMounted(syncIndeterminate)
watch(() => props.indeterminate, syncIndeterminate)

const checked = computed(() =>
  Array.isArray(props.modelValue)
    ? props.modelValue.includes(props.value as string | number)
    : !!props.modelValue,
)

function onChange(event: Event) {
  const isChecked = (event.target as HTMLInputElement).checked

  if (!Array.isArray(props.modelValue)) {
    emit('update:modelValue', isChecked)
    return
  }

  const next = props.modelValue.filter((v) => v !== props.value)
  if (isChecked) next.push(props.value as string | number)
  emit('update:modelValue', next)
}
</script>

<template>
  <input
    v-if="bare"
    ref="el"
    :id="controlId"
    type="checkbox"
    :checked="checked"
    :disabled="disabled"
    :class="[CHOICE_CONTROL, 'rounded', rootClass]"
    @change="onChange"
    v-bind="controlAttrs"
  />

  <div v-else :class="['flex items-start gap-2.5', rootClass]">
    <input
      ref="el"
      :id="controlId"
      type="checkbox"
      :checked="checked"
      :disabled="disabled"
      :class="[CHOICE_CONTROL, 'rounded mt-0.5']"
      @change="onChange"
      v-bind="controlAttrs"
    />
    <label v-if="label || $slots.default" :for="controlId" class="cursor-pointer select-none">
      <span class="block text-sm text-gray-700" :class="{ 'opacity-50': disabled }">
        <slot>{{ label }}</slot>
      </span>
      <span v-if="description" class="block text-xs text-gray-500 mt-0.5">{{ description }}</span>
    </label>
  </div>
</template>
